import type { Reducer } from '@reduxjs/toolkit';
import { createAction } from '@reduxjs/toolkit';
import type { DeepReadonly } from 'ts-essentials';

/* eslint-disable-next-line no-restricted-imports */
import type { Id, Store } from '@jane/shared/models';

import { NotificationsService } from '../../services/notifications';
import { StoreSource } from '../../sources/store';
import type { CustomerAction, CustomerThunkAction } from '../customerAction';
import type { AppState } from '../redux';
import { getCart } from './cart';

export const SET_DISABLE_AUTH_FEATURES =
  'embedded-app/set-disable-auth-features';
export const setDisableAuthFeatures = createAction<
  boolean,
  typeof SET_DISABLE_AUTH_FEATURES
>(SET_DISABLE_AUTH_FEATURES);

const SET_PARTNER = 'embedded-app/set-partner';
export const setPartner = createAction<
  { id: Id; name: string },
  typeof SET_PARTNER
>(SET_PARTNER);

export const VERIFY_STORE = 'embedded-app/verify-store';
const initiateVerifyStore = createAction<
  {
    storeId: Id;
  },
  typeof VERIFY_STORE
>(VERIFY_STORE);

export const VERIFY_STORE_SUCCESS = 'embedded-app/verify-store-success';
export const verifyStoreSuccess = createAction<
  {
    isPartnerHosted: boolean;
    store: Store;
  },
  typeof VERIFY_STORE_SUCCESS
>(VERIFY_STORE_SUCCESS);

export const VERIFY_STORE_ERROR = 'embedded-app/verify-store-error';
const verifyStoreFailure = createAction<void, typeof VERIFY_STORE_ERROR>(
  VERIFY_STORE_ERROR
);

const verifyStoreError = (error: string): CustomerThunkAction => {
  return (dispatch) => {
    dispatch(verifyStoreFailure());
    return NotificationsService.error(error);
  };
};

type VerifyStoreOptions = {
  forceEmbed?: boolean;
  framelessEmbed?: boolean;
  isPartnerHosted?: boolean;
  operatorEmbed?: boolean;
};
export const verifyStore =
  ({
    storeId,
    options = {},
  }: {
    options?: VerifyStoreOptions;
    storeId: Id;
  }): CustomerThunkAction =>
  (dispatch) => {
    dispatch(initiateVerifyStore({ storeId }));

    const { isPartnerHosted = false } = options;

    const disableShopping =
      window.location.search.includes('disableShopping=1');

    return StoreSource.get(storeId).then(
      (response) => {
        if (!response || !response.store) {
          return dispatch(verifyStoreError('Store not found'));
        }

        const { store } = response;
        const overriddenStore = {
          ...store,
          hide_prices: store.hide_prices || disableShopping,
        };

        dispatch(
          verifyStoreSuccess({
            store: overriddenStore,
            isPartnerHosted,
          })
        );

        dispatch(getCart());
      },
      (err) => dispatch(verifyStoreError(err))
    );
  };

export type EmbeddedAppActions =
  | ReturnType<typeof setDisableAuthFeatures>
  | ReturnType<typeof setPartner>
  | ReturnType<typeof initiateVerifyStore>
  | ReturnType<typeof verifyStoreSuccess>
  | ReturnType<typeof verifyStoreFailure>;

export type EmbeddedAppState = DeepReadonly<{
  disableAuthFeatures: boolean;
  hasVerified: boolean;
  isPartnerHosted: boolean;
  isVerifying: boolean;
  partnerId: Id | undefined;
  partnerName: string | undefined;
}>;

const getInitialState = (): EmbeddedAppState => ({
  partnerId: undefined,
  partnerName: undefined,
  hasVerified: false,
  isVerifying: false,
  isPartnerHosted: false,
  disableAuthFeatures: false,
});

export const embeddedAppReducer: Reducer<EmbeddedAppState, CustomerAction> = (
  state = getInitialState(),
  action
) => {
  switch (action.type) {
    case SET_DISABLE_AUTH_FEATURES:
      return { ...state, disableAuthFeatures: action.payload };
    case SET_PARTNER:
      return {
        ...state,
        partnerId: action.payload.id,
        partnerName: action.payload.name,
      };
    case VERIFY_STORE:
      return { ...state, isVerifying: true, partnerId: action.payload.storeId };
    case VERIFY_STORE_SUCCESS: {
      const { store, isPartnerHosted } = action.payload;
      if (state.partnerId !== store.id) {
        return {
          ...state,
          isVerifying: false,
          partnerId: undefined,
        };
      }

      return {
        ...state,
        partnerName: store.name,
        isVerifying: false,
        hasVerified: true,
        isPartnerHosted,
      };
    }
    case VERIFY_STORE_ERROR:
      return { ...state, isVerifying: false, partnerId: undefined };
  }

  return state;
};

export const selectEmbeddedApp = (state: AppState) => state.embeddedApp;
